import { useEffect } from 'react';

/**
 * Ask the browser to confirm before it discards unsaved work.
 *
 * Covers the exits that leave the page entirely: closing the tab, reloading,
 * and navigating away to somewhere outside the app. It does NOT cover moving
 * around inside the app — see the note below.
 *
 * The listener is attached only while there is something to lose, and removed
 * the moment there is not. That ordering is the whole point: a confirmation
 * that fires on a clean page teaches people to dismiss it without reading, and
 * then it no longer protects anything.
 *
 * The browser owns the wording and will not show anything we pass it. Chrome,
 * Firefox and Safari all display their own generic sentence, so there is no
 * point building a message here.
 *
 * ⚠️ In-app navigation is not covered. Blocking a route change needs
 * react-router's blocker, which only works under a data router
 * (createBrowserRouter). This app mounts BrowserRouter in src/index.tsx, so the
 * blocker is unavailable until that migrates. Tracked separately — do not
 * "fix" this by scattering confirmation dialogs across the sidebar and the
 * campaign switcher, because two guards that disagree are worse than one.
 *
 * @param hasUnsavedChanges whether the screen currently holds work that a
 *   reload would destroy. Derive it from the editor's own state rather than
 *   tracking it a second time, so the two cannot drift apart.
 */
const useBeforeUnloadWarning = (hasUnsavedChanges: boolean): void => {
  useEffect(() => {
    if (!hasUnsavedChanges) return;

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      // Both lines are required. preventDefault() is the modern signal and
      // returnValue is what older browsers still read; setting only one of them
      // means the prompt silently does not appear in part of our user base.
      event.preventDefault();
      event.returnValue = '';
      return '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);
};

export default useBeforeUnloadWarning;
